/**
 * Copyright 2021-2022 Highway9 Networks Inc.
 */
import { DataObject, ObjectReference } from "./dataObject";
import Radio, { type ImageInfo} from "./radio";
import { MarkerData } from "@h9/ui-lib";
import { PlanCoods } from "~/components/shared/d3/FloorPlan";
import { FREQUENCY_SELECTION_LOGIC } from "~/constants";

export interface CalibrationPoints {
  floorPlan?: ImageInfo;
  rfPlan?: ImageInfo;
  sinrPlan?: ImageInfo;
  dominantCellPlan?: ImageInfo;
  customPlan?: {
    imagesInfo: ImageInfo;
    planName: string;
  }[];
  map: PlanMapData;
}

export enum GroupPhaseType {
  PLANNING = "PLANNING",
  SURVEY = "SURVEY",
  INSTALLATION = "INSTALLATION",
  TESTING = "TESTING",
  PROVISIONED = "PROVISIONED",
}
export interface RadioGroup extends DataObject {
  id?: string;
  name: string;
  docVersion?: number;
  description?: string;
  /**@enum {string} "INDOOR" | "OUTDOOR";*/
  zoneType: string;
  radioSite: ObjectReference;

  overrideMacroEnbIdList?: boolean;
  macroEnbIdList?: number[];
  mobilityProfile: ObjectReference | null;
  technology: string;

  map?: Location[];
  // location: Location;
  frequency: Frequency;
  sas: SAS;
  datapath: {
    localExit: LocalExit;
  };
  locationSyncMode: string;
  clockPool?: ObjectReference;
  // Group Phase Lifecycle Stage
  lifecycleStage?: GroupPhaseType;


  // merge with radioGroup
  floor?: number;
  area?: number;
  plan?: {
    calibrationPoints: CalibrationPoints;
  };
  countAPs?: number;
  connectedAPs?: number;
  radios?: Radio[];
  radioCoordinates?: MarkerData[];
  radioFloorPlanCoods?: PlanCoods[];
  overallStatus?: string;
  
  /**Not Available in the API */
/*Whenever (_)underscore used that means value manually pushed into API from UI  */
  _plmnList?: string[];
  _vendorList?: string[];

  // Not in API added from UI to preserve state of manual profile selection in case of auto recommendation of profiles.
  _manualProfileSelection?: boolean;

}


interface Location {
  latitude: number;
  longitude: number;
  zoom?: number;
  name?: string;
  // ntpServer1: string;
  // ntpServer2: string;
  // ntpPort1: number;
  // ntpPort2: number;
}

interface Frequency {
  band: string;
  bandwidth: number;
  // bandwidth : 5 | 10 | 15 | 20;
  subframeAssignment: number;
  specialSubframePattern: number;
}

interface SAS {
  sasEnabled: boolean;
  sasUserId: string;
  frequencySelectionLogic: string;
  // frequencySelectionLogic : 'Power' | "Bandwidth" | 'Frequency';
  allowOnlySelectedFrequency: boolean;
}

interface LocalExit {
  enabled: boolean;
  mode: string;
  ipPool: string;
  ipPoolNetmask: string;
  enableStaticIp: boolean;
  staticIpStart: string;
  staticIpEnd: string;
}

interface CalibrationPointsMap {
  id?: string;
  latitude: number;
  longitude: number;
  name?: string;
}

export interface PlanMapData {
  points: CalibrationPointsMap[];
  zoom?: number;
  center?: CalibrationPointsMap;
  heading: number;
  view?: string;
  opacity?: number;
}


export const defaultRadioGroup = {
  name: "",
  description: "",
  zoneType: "INDOOR",
  technology: "4G",
  radioSite: {
    id: "",
    name: "",
  },
  sas: {
    sasEnabled: true,
    sasUserId: "",
    frequencySelectionLogic: FREQUENCY_SELECTION_LOGIC.PBF,
    allowOnlySelectedFrequency: false,
  },
  datapath: {
    localExit: {
      enabled: false,
      mode: "NAT",
      ipPool: "10.0.0.0",
      enableStaticIp: false,
      staticIpStart: "",
      staticIpEnd: "",
      ipPoolNetmask: "255.255.255.0",
    },
  },

  frequency: {
    band: "B48",
    bandwidth: 20,
    specialSubframePattern: 7,
    subframeAssignment: 1,
  },
  mobilityProfile: null,
  locationSyncMode: "PTP",
  clockPool: undefined,

  floor: 1,
  area : undefined,
  plan: {
    calibrationPoints: {
      map: {
        heading: 0,
        zoom: 18,
        points: [],
        view: "HYBRID",
      },
    },
  },
  lifecycleStage: undefined,
};